import { DeleteIcon } from "@chakra-ui/icons"
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react"
import type { ReactNode } from "react"
import Select from "@/components/Common/AppSelect"

type WarehouseOption = {
  id: string
  code?: string
  name?: string
}

type WarehouseFieldProps = {
  value: string
  onChange: (value: string) => void
  options: WarehouseOption[]
  isDisabled?: boolean
  label?: string
  placeholder?: string
  size?: "sm" | "md" | "lg"
  maxW?: string
}

type DualWarehouseFieldsProps = {
  fromValue: string
  toValue: string
  onFromChange: (value: string) => void
  onToChange: (value: string) => void
  options: WarehouseOption[]
  fromLabel?: string
  toLabel?: string
  fromPlaceholder?: string
  toPlaceholder?: string
}

type DescriptionFieldProps = {
  value: string
  onChange: (value: string) => void
  label?: string
  placeholder?: string
}

type SourceDocumentFieldProps = {
  sourceDocumentValue: string
  onSourceDocumentChange: (value: string) => void
  sourceDocumentLabel?: string
  sourceDocumentPlaceholder?: string
}

type FooterActionsProps = {
  onCancel?: () => void
  onSubmit?: () => void
  submitLabel: string
  isSubmitDisabled?: boolean
  isSubmitLoading?: boolean
  submitType?: "button" | "submit"
}

type RowProductSelectorProps = {
  value: string
  onOpenPicker: () => void
  onClear?: () => void
  showLabel?: boolean
  isRequired?: boolean
  isInvalid?: boolean
  label?: string
  placeholder?: string
  mobileLabel?: string
  clearAriaLabel?: string
  minW?: string
  size?: "sm" | "md" | "lg"
}

export function SingleWarehouseField({
  value,
  onChange,
  options,
  isDisabled,
  label = "Magazyn",
  placeholder = "Wybierz magazyn",
  size = "md",
  maxW,
}: WarehouseFieldProps) {
  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Select
        size={size}
        maxW={maxW}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        isDisabled={isDisabled}
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.code} — {option.name}
          </option>
        ))}
      </Select>
    </FormControl>
  )
}

export function SingleWarehouseDescriptionField({
  value,
  onChange,
  label = "Opis",
  placeholder = "Opcjonalnie",
}: DescriptionFieldProps) {
  return (
    <Box>
      <Text fontWeight="semibold" mb={1}>
        {label}
      </Text>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </Box>
  )
}

export function SourceDocumentField({
  sourceDocumentValue,
  onSourceDocumentChange,
  sourceDocumentLabel = "Dokument źródłowy",
  sourceDocumentPlaceholder = "np. FV/123",
}: SourceDocumentFieldProps) {
  return (
    <FormControl>
      <FormLabel>{sourceDocumentLabel}</FormLabel>
      <Input
        value={sourceDocumentValue}
        onChange={(e) => onSourceDocumentChange(e.target.value)}
        placeholder={sourceDocumentPlaceholder}
      />
    </FormControl>
  )
}

export function DualWarehouseFields({
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  options,
  fromLabel = "Z magazynu",
  toLabel = "Na magazyn",
  fromPlaceholder = "Wybierz magazyn źródłowy",
  toPlaceholder = "Wybierz magazyn docelowy",
}: DualWarehouseFieldsProps) {
  return (
    <Stack
      direction={{ base: "column", md: "row" }}
      spacing={4}
      align="stretch"
    >
      <Box flex="1 1 0" minW="0">
        <FormControl>
          <FormLabel>{fromLabel}</FormLabel>
          <Select
            size="sm"
            value={fromValue}
            onChange={(e) => onFromChange(e.target.value)}
            placeholder={fromPlaceholder}
          >
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.code} — {option.name}
              </option>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box flex="1 1 0" minW="0">
        <FormControl>
          <FormLabel>{toLabel}</FormLabel>
          <Select
            size="sm"
            value={toValue}
            onChange={(e) => onToChange(e.target.value)}
            placeholder={toPlaceholder}
          >
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.code} — {option.name}
              </option>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Stack>
  )
}

export function SingleWarehouseFormFooter({
  onCancel,
  onSubmit,
  submitLabel,
  isSubmitDisabled,
  isSubmitLoading,
  submitType = "button",
}: FooterActionsProps) {
  return (
    <HStack justify="flex-end">
      {onCancel ? (
        <Button variant="ghost" onClick={onCancel}>
          Anuluj
        </Button>
      ) : null}
      <Button
        colorScheme="green"
        onClick={onSubmit}
        type={submitType}
        isDisabled={isSubmitDisabled}
        isLoading={isSubmitLoading}
      >
        {submitLabel}
      </Button>
    </HStack>
  )
}

export function RowProductSelector({
  value,
  onOpenPicker,
  onClear,
  showLabel = true,
  isRequired,
  isInvalid,
  label = "Produkt",
  placeholder = "Nie wybrano produktu",
  mobileLabel,
  clearAriaLabel = "Wyczyść produkt",
  minW = "0",
  size = "md",
}: RowProductSelectorProps) {
  return (
    <FormControl isRequired={isRequired} isInvalid={isInvalid} minW={minW}>
      {showLabel ? (
        <FormLabel
          display={mobileLabel ? { base: "block", md: "none" } : undefined}
        >
          {mobileLabel ?? label}
        </FormLabel>
      ) : null}
      <HStack w="100%" minW={0} spacing={2}>
        <Input
          size={size}
          readOnly
          onClick={onOpenPicker}
          placeholder={placeholder}
          value={value}
          minW={0}
          flex="1 1 auto"
          fontSize="xs"
          px={2}
          cursor="pointer"
        />
        {onClear ? (
          <IconButton
            size={size}
            aria-label={clearAriaLabel}
            icon={<DeleteIcon />}
            colorScheme="red"
            variant="ghost"
            onClick={onClear}
            flexShrink={0}
            minW="32px"
            fontSize="xs"
          />
        ) : null}
      </HStack>
    </FormControl>
  )
}

export function SingleWarehouseSectionGroup({
  children,
}: {
  children: ReactNode
}) {
  return <>{children}</>
}
